import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import { getAllBarsApi, getState } from "../../api/api";
import useCitiesByState from "../../hooks/useCitiesByState";
import RIcon from "./components/ReservasIcons";
import BarCard from "./components/BarCard";
import QuickTableReservationModal from "./components/QuickTableReservationModal";
import { RATING_RANGE_OPTIONS, ratingRangeLabel, ratingRangesParam } from "./components/reviewsModel";
import { isExplicitDateTime, runLatestRequest } from "./components/quickReservationModel";
import OrganizerTimePillSelect from "../../organizer/components/OrganizerTimePillSelect";
import { SingleDatePickerPill, PillDropdown, MultiPillDropdown, ReservationKindTabs, formatPillDateLabel } from "./components/ReservaShared";
import "./reservas.css";

const normalizeBars = (response) => Array.isArray(response?.data?.info) ? response.data.info : [];

const Reservas = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth?.accessToken);

  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState(null);
  const [quickSelection, setQuickSelection] = useState(null);
  const barsRequestOwner = useRef(0);

  // Filter state
  const [nameFilter, setNameFilter] = useState("");
  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => dayjs().add(1, "day").startOf("day"));
  const [timeFilter, setTimeFilter] = useState("");
  // Selected rating-range tokens (`4-5`, `3-4`, `0-3`), sent as the CSV `ratingRanges`.
  const [ratingRanges, setRatingRanges] = useState([]);

  const [states, setStates] = useState([]);
  // Cities depend on the selected province: never load the full catalog.
  const { cities } = useCitiesByState(stateId);

  const minDate = dayjs().startOf("day");
  const maxDate = minDate.add(60, "day");

  const loadCatalogs = useCallback(async () => {
    try {
      const stRes = await getState();
      setStates(Array.isArray(stRes?.data?.info) ? stRes.data.info : []);
    } catch (_) { }
  }, []);

  const loadBars = useCallback(async (overrideParams = {}) => {
    const own = (key) => Object.prototype.hasOwnProperty.call(overrideParams, key);
    const date = own("date") ? overrideParams.date : selectedDate?.format("YYYY-MM-DD") || "";
    const time = own("time") ? overrideParams.time : timeFilter;
    const rating = ratingRangesParam(own("ratingRanges") ? overrideParams.ratingRanges : ratingRanges);
    // The backend caps the list (`limit`), so the name is filtered there, before the cap.
    const name = (own("name") ? overrideParams.name : nameFilter).trim();
    const context = { date, time, explicitDateTime: isExplicitDateTime(date, time) };
    const params = {
      ...(own("cityId") ? overrideParams.cityId : cityId) && { cityid: own("cityId") ? overrideParams.cityId : cityId },
      ...(own("stateId") ? overrideParams.stateId : stateId) && { stateid: own("stateId") ? overrideParams.stateId : stateId },
      ...(name && { name }),
      ...(date && { date }), ...(time && { time }), ...(rating && { ratingRanges: rating }),
    };
    return runLatestRequest(barsRequestOwner, () => getAllBarsApi(params), context, {
      start: () => { setLoading(true); setError(""); setSubmittedSearch(null); setQuickSelection(null); },
      success: (response, submitted) => { setBars(normalizeBars(response)); setSubmittedSearch(submitted); },
      error: (err) => { setBars([]); setError(err?.response?.data?.message || "No pudimos cargar los bares disponibles."); },
      finish: () => setLoading(false),
    });
  }, [cityId, stateId, nameFilter, timeFilter, selectedDate, ratingRanges]);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  // Initial load, and a debounced reload while the name is typed. The local `filteredBars` filter
  // gives instant feedback in the meantime; `runLatestRequest` drops stale responses.
  const isFirstLoad = useRef(true);
  useEffect(() => {
    const delay = isFirstLoad.current ? 0 : 350;
    isFirstLoad.current = false;
    const timer = setTimeout(() => loadBars({ name: nameFilter }), delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameFilter]);

  const handleStateChange = (val) => {
    setStateId(val);
    setCityId(""); // Reset selected city; its options reload through useCitiesByState
    loadBars({ stateId: val, cityId: "" });
  };

  const handleCityChange = (val) => {
    setCityId(val);
    loadBars({ cityId: val });
  };

  const handleRatingChange = (val) => {
    setRatingRanges(val);
    loadBars({ ratingRanges: val });
  };

  const filteredBars = useMemo(() => {
    if (!nameFilter.trim()) return bars;
    return bars.filter((b) => (b.name || "").toLowerCase().includes(nameFilter.toLowerCase()));
  }, [bars, nameFilter]);

  const handleSearch = () => loadBars({ date: selectedDate?.format("YYYY-MM-DD") || "", time: timeFilter });
  const invalidateQuick = () => { setSubmittedSearch(null); setQuickSelection(null); };
  const handleDateChange = (value) => { setSelectedDate(value); if (!value) invalidateQuick(); };
  const handleTimeChange = (value) => { setTimeFilter(value); if (!value) invalidateQuick(); };
  const openQuick = (bar, slot, invoker) => setQuickSelection({ bar, slot, invoker, date: submittedSearch.date });
  const handoff = (preset) => navigate(`/reservas/${preset.bar.slug || preset.bar._id}`, { state: { presetSelection: {
    flow: "bar_quick_reservation", bar: preset.bar, dateLabel: preset.date, selectedSlot: preset.slot,
    partySize: preset.partySize, selectedZone: preset.selectedZone, holdId: preset.holdId, holdExpiresAt: preset.holdExpiresAt,
  } } });
  const requireLogin = (pending) => navigate("/login", { state: { from: "/reservas", quickSelection: pending } });

  // One removable chip per active filter, in the same order as the filter bar.
  const chips = [];
  const stateName = states.find((s) => s._id === stateId)?.name;
  const cityName = cities.find((c) => c._id === cityId)?.name;
  if (nameFilter.trim()) chips.push({ icon: "search", label: `“${nameFilter.trim()}”`, onClear: () => setNameFilter("") });
  if (stateName) chips.push({ icon: "map", label: stateName, onClear: () => handleStateChange("") });
  if (cityName) chips.push({ icon: "pin", label: cityName, onClear: () => handleCityChange("") });
  if (selectedDate) chips.push({ icon: "cal", label: formatPillDateLabel(selectedDate), onClear: () => handleDateChange(null) });
  if (timeFilter) chips.push({ icon: "clock", label: `${timeFilter} hs`, onClear: () => handleTimeChange("") });
  ratingRanges.forEach((range) => chips.push({ icon: "star", label: `Valoración ${ratingRangeLabel(range)}`, onClear: () => handleRatingChange(ratingRanges.filter((r) => r !== range)) }));

  return (
    <div className="reservas-root">
      <div className="wrap">
        <ReservationKindTabs active="mesas" />

        {/* Header */}
        <div className="res-head">
          <div className="res-actions">
            <div>
              <div className="eyebrow">Mesas</div>
              <h1>Reservá tu mesa</h1>
            </div>
          </div>
          <p className="lead">
            Encontrá el bar perfecto y asegurá tu mesa en segundos. Sin cargos por servicio.
            El precio que ves es el que pagás.
          </p>
        </div>

        {/* Filters */}
        <div className="res-filters res-filters--rating">
          <div className="rf">
            <label>Nombre del bar</label>
            <div className="rf-control">
              <RIcon name="search" />
              <input
                placeholder="Opcional"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
              />
            </div>
          </div>
          <div className="rf">
            <label>Provincia</label>
            <PillDropdown
              iconName="map"
              value={stateId}
              options={[{ value: "", label: "Todas" }, ...states.map((s) => ({ value: s._id, label: s.name }))]}
              onChange={handleStateChange}
              placeholder="Todas"
              ariaLabel="Provincia"
            />
          </div>
          <div className="rf">
            <label>Ciudad</label>
            <PillDropdown
              iconName="pin"
              value={cityId}
              options={[{ value: "", label: "Todas" }, ...cities.map((c) => ({ value: c._id, label: c.name }))]}
              onChange={handleCityChange}
              placeholder="Todas"
              searchable={true}
              disabled={!stateId}
              ariaLabel="Ciudad"
            />
          </div>
          <div className="rf">
            <label>Fecha</label>
            <SingleDatePickerPill
              value={selectedDate}
              onChange={handleDateChange}
              minDate={minDate}
              maxDate={maxDate}
              ariaLabel="Fecha"
            />
          </div>
          <div className="rf">
            <label>Horario</label>
            <OrganizerTimePillSelect
              value={timeFilter}
              onChange={handleTimeChange}
              placeholder="--:--"
              triggerClassName="ef-date-pill-trigger"
              ariaLabel="Horario"
              minutes={["00", "30"]}
              singleList={true}
            />
          </div>
          <div className="rf">
            <label>Valoración</label>
            <MultiPillDropdown
              iconName="star"
              value={ratingRanges}
              options={RATING_RANGE_OPTIONS}
              onChange={handleRatingChange}
              placeholder="Todas"
              ariaLabel="Valoración"
            />
          </div>
          <div className="rf rf-search">
            <button className="r-btn-primary" onClick={handleSearch}>
              <RIcon name="search" width={17} height={17} /> Buscar mesas
            </button>
          </div>
        </div>

        {/* Result meta */}
        <div className="res-meta">
          <div className="count">
            <b>{filteredBars.length} bares</b> con mesas disponibles
          </div>
          {chips.length > 0 && (
            <div className="chips">
              {chips.map((c, i) => (
                <span className="q-chip" key={i} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <RIcon name={c.icon} width={13} height={13} />
                  {c.label}
                  <button
                    type="button"
                    aria-label={`Quitar filtro ${c.label}`}
                    onClick={c.onClear}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      marginLeft: "4px",
                      color: "var(--zoco-gray-2)",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center"
                    }}
                  >
                    <RIcon name="x" width={10} height={10} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spinner animation="border" />
          </div>
        ) : filteredBars.length === 0 ? (
          <div className="empty-state">
            <RIcon name="glass" width={48} height={48} />
            <h3>No encontramos bares</h3>
            <p>Probá con otros filtros o volvé más tarde.</p>
          </div>
        ) : (
          <div className="bars-grid">{filteredBars.map((bar) => <BarCard key={String(bar._id)} bar={bar}
            showQuickSlots={submittedSearch?.explicitDateTime} onSelectSlot={openQuick}
            onNavigate={(slugOrId) => navigate(`/reservas/${slugOrId}`)} />)}</div>
        )}
        {quickSelection && <QuickTableReservationModal selection={quickSelection} token={token}
          onClose={() => setQuickSelection(null)} onHandoff={handoff} onRequireLogin={requireLogin} />}
        <div style={{ height: 60 }} />
      </div>
    </div>
  );
};

export default Reservas;
